/**
 * DatabricksBulkAnalyzeProgress
 *
 * Modal that opens after the parent triggers a bulk analyze. Posts to
 * /api/integrations/databricks/bulk-analyze, then opens an SSE stream
 * to /bulk-analyze/:batchId/stream and renders per-table status as
 * events arrive. On completion, surfaces success/failure summary plus
 * a cross-link to the Bulk Decision matrix (already shipped under HITL).
 *
 * Engineer-grade UI — design pass later. Inline styles match the dark
 * theme used in BulkEvaluatePage.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useSseStream, type SseEvent } from '../../hooks/useSseStream';

// ============================================================================
// Types
// ============================================================================

export interface BulkAnalyzeTable {
  catalog: string;
  schema: string;
  table: string;
}

export type AnalysisType = 'data_quality' | 'ml_readiness' | 'ontology_inference';

interface DatabricksBulkAnalyzeProgressProps {
  connectionId: string;
  tables: BulkAnalyzeTable[];
  analysisTypes?: AnalysisType[];
  sampleSize?: number;
  journeyId?: string;
  onClose: () => void;
  /**
   * Optional override of the cross-link. Default: link to the Bulk
   * Decision matrix at the most-specific common scope (catalog or schema).
   * Pass null to suppress the cross-link entirely.
   */
  bulkDecisionLink?: string | null;
}

type TableStatus = 'pending' | 'running' | 'done' | 'failed';

interface PerTableState {
  status: TableStatus;
  jobId?: string;
  error?: string;
}

interface BulkAnalysisSummary {
  batchId: string;
  totalTables: number;
  succeeded: number;
  failed: number;
  durationMs: number;
}

// ============================================================================
// Helpers
// ============================================================================

function tableKey(t: BulkAnalyzeTable): string {
  return `${t.catalog}.${t.schema}.${t.table}`;
}

/**
 * Compute the most-specific common scope across the selected tables.
 * - If all share a catalog AND schema → schema scope.
 * - If all share a catalog only → catalog scope.
 * - Otherwise → null (no cross-link).
 */
function commonScopeLink(tables: BulkAnalyzeTable[]): string | null {
  if (tables.length === 0) return null;
  const firstCatalog = tables[0].catalog;
  const firstSchema = tables[0].schema;
  const allSameCatalog = tables.every((t) => t.catalog === firstCatalog);
  if (!allSameCatalog) return null;
  const allSameSchema = tables.every((t) => t.schema === firstSchema);
  if (allSameSchema) {
    return `/scope/schema/${encodeURIComponent(`${firstCatalog}.${firstSchema}`)}/evaluate`;
  }
  return `/scope/catalog/${encodeURIComponent(firstCatalog)}/evaluate`;
}

// ============================================================================
// Component
// ============================================================================

export const DatabricksBulkAnalyzeProgress: React.FC<DatabricksBulkAnalyzeProgressProps> = ({
  connectionId,
  tables,
  analysisTypes = ['data_quality'],
  sampleSize,
  journeyId,
  onClose,
  bulkDecisionLink,
}) => {
  const { accessToken } = useAuth();
  const [batchId, setBatchId] = useState<string | null>(null);
  const [perTable, setPerTable] = useState<Record<string, PerTableState>>(() => {
    const initial: Record<string, PerTableState> = {};
    for (const t of tables) initial[tableKey(t)] = { status: 'pending' };
    return initial;
  });
  const [summary, setSummary] = useState<BulkAnalysisSummary | null>(null);
  const [postError, setPostError] = useState<string | null>(null);
  const [streamError, setStreamError] = useState<string | null>(null);

  // DATA-INTEGRITY FIX: a stable primitive key for the request inputs. The
  // effect deps previously listed the `tables` and `analysisTypes` arrays
  // directly; `analysisTypes` defaults to a fresh `['data_quality']` literal
  // every render (and parents often pass new array refs), so the effect re-ran
  // on every render and fired a NEW bulk-analyze batch each time — duplicate
  // batches, duplicate billed work, racing streams. Keying on the serialized
  // content + a ref guard makes the POST fire exactly once per distinct input.
  const inputKey = `${connectionId}|${tables.map(tableKey).join('~')}|${analysisTypes.join(',')}|${sampleSize ?? ''}|${journeyId ?? ''}`;
  const startedKeyRef = useRef<string | null>(null);

  // POST /bulk-analyze once per distinct input set.
  useEffect(() => {
    if (!accessToken) return;
    if (startedKeyRef.current === inputKey) return;
    startedKeyRef.current = inputKey;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/api/integrations/databricks/bulk-analyze?connectionId=${encodeURIComponent(connectionId)}`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ tables, analysisTypes, sampleSize, journeyId }),
          },
        );
        if (!res.ok) {
          const body = await res.text().catch(() => '');
          throw new Error(`Failed to start bulk analyze (HTTP ${res.status}): ${body}`);
        }
        const data = (await res.json()) as { batchId: string };
        if (!cancelled) setBatchId(data.batchId);
      } catch (err) {
        if (!cancelled) setPostError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
    // inputKey is a stable serialization of accessToken-gated request inputs
    // (connectionId/tables/analysisTypes/sampleSize/journeyId); the ref guard
    // makes re-entry idempotent. Listing the raw arrays here is what caused the
    // re-fire loop this fix removes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, inputKey]);

  // Open the SSE stream once we have a batchId.
  const streamUrl = batchId
    ? `/api/integrations/databricks/bulk-analyze/${encodeURIComponent(batchId)}/stream`
    : null;

  useSseStream({
    url: streamUrl,
    accessToken,
    onEvent: (event: SseEvent) => {
      try {
        const payload = JSON.parse(event.data);
        if (event.type === 'progress') {
          const path: string = payload.tablePath;
          if (payload.type === 'started') {
            setPerTable((prev) => ({ ...prev, [path]: { ...prev[path], status: 'running' } }));
          } else if (payload.type === 'completed') {
            setPerTable((prev) => ({
              ...prev,
              [path]: { ...prev[path], status: 'done', jobId: payload.jobId },
            }));
          } else if (payload.type === 'failed') {
            setPerTable((prev) => ({
              ...prev,
              [path]: { ...prev[path], status: 'failed', error: payload.error },
            }));
          }
        } else if (event.type === 'complete') {
          setSummary(payload as BulkAnalysisSummary);
        } else if (event.type === 'error') {
          setStreamError(payload.message ?? 'Stream error');
        }
      } catch {
        // Skip events that fail to parse — server should always send valid JSON.
      }
    },
    onError: (err) => setStreamError(err.message),
  });

  const counts = useMemo(() => {
    let pending = 0;
    let running = 0;
    let done = 0;
    let failed = 0;
    for (const s of Object.values(perTable)) {
      if (s.status === 'pending') pending += 1;
      else if (s.status === 'running') running += 1;
      else if (s.status === 'done') done += 1;
      else if (s.status === 'failed') failed += 1;
    }
    return { pending, running, done, failed, total: tables.length };
  }, [perTable, tables.length]);

  const isComplete = summary !== null;
  const cross = bulkDecisionLink === null
    ? null
    : (bulkDecisionLink ?? commonScopeLink(tables));

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="bulk-analyze-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: 'var(--tri-card)',
          color: 'var(--tri-ink)',
          width: 'min(720px, 90vw)',
          maxHeight: '90vh',
          borderRadius: 12,
          padding: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
          border: '1px solid var(--tri-taupe)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 id="bulk-analyze-title" style={{ margin: 0, fontSize: '1.25rem' }}>
              Bulk FAIR analysis
            </h2>
            <p aria-live="polite" style={{ margin: '4px 0 0 0', color: 'var(--tri-body)', fontSize: '0.85rem' }}>
              {counts.done + counts.failed} of {counts.total} done
              {' · '}
              <span style={{ color: 'var(--tri-good)' }}>{counts.done} succeeded</span>
              {' · '}
              <span style={{ color: counts.failed > 0 ? 'var(--tri-hard)' : 'var(--tri-body)' }}>
                {counts.failed} failed
              </span>
              {counts.running > 0 && (
                <>
                  {' · '}
                  <span style={{ color: 'var(--tri-teal)' }}>{counts.running} running</span>
                </>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={!isComplete && !postError && !streamError}
            style={{
              background: 'transparent',
              color: 'var(--tri-body)',
              border: '1px solid var(--tri-taupe)',
              borderRadius: 6,
              padding: '6px 12px',
              cursor: !isComplete && !postError && !streamError ? 'not-allowed' : 'pointer',
              fontSize: '0.85rem',
            }}
            title={!isComplete && !postError && !streamError ? 'Wait for batch to complete' : 'Close'}
          >
            Close
          </button>
        </div>

        {postError && (
          <div
            role="alert"
            style={{
              background: 'rgba(248,113,113,0.15)',
              border: '1px solid rgba(248,113,113,0.4)',
              padding: 12,
              borderRadius: 8,
              fontSize: '0.85rem',
              color: 'var(--tri-hard)',
            }}
          >
            <strong>Failed to start batch:</strong> {postError}
          </div>
        )}
        {streamError && (
          <div
            role="alert"
            style={{
              background: 'rgba(245,158,11,0.15)',
              border: '1px solid rgba(245,158,11,0.4)',
              padding: 12,
              borderRadius: 8,
              fontSize: '0.85rem',
              color: 'var(--tri-soft)',
            }}
          >
            <strong>Stream interrupted:</strong> {streamError}
          </div>
        )}

        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            overflow: 'auto',
            maxHeight: '50vh',
            border: '1px solid var(--tri-taupe)',
            borderRadius: 8,
          }}
        >
          {tables.map((t) => {
            const key = tableKey(t);
            const state = perTable[key] ?? { status: 'pending' as TableStatus };
            return (
              <li
                key={key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 14px',
                  borderBottom: '1px solid var(--tri-taupe)',
                  fontFamily: 'JetBrains Mono, Consolas, monospace',
                  fontSize: '0.85rem',
                }}
              >
                <StatusGlyph status={state.status} />
                <span style={{ flex: 1, color: 'var(--tri-ink)' }}>{key}</span>
                {state.status === 'failed' && state.error && (
                  <span
                    style={{ color: 'var(--tri-hard)', fontSize: '0.75rem' }}
                    title={state.error}
                  >
                    {truncate(state.error, 50)}
                  </span>
                )}
                {state.status === 'done' && state.jobId && (
                  <a
                    href={`/analysis/${state.jobId}/evaluate`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: 'var(--tri-teal)', fontSize: '0.75rem', textDecoration: 'none' }}
                  >
                    View →
                  </a>
                )}
              </li>
            );
          })}
        </ul>

        {isComplete && (
          <div
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingTop: 4,
            }}
          >
            <span style={{ color: 'var(--tri-body)', fontSize: '0.8rem' }}>
              Completed in {(summary.durationMs / 1000).toFixed(1)}s
            </span>
            {cross && counts.done > 0 && (
              <a
                href={cross}
                style={{
                  background: 'rgba(168,85,247,0.15)',
                  color: 'var(--tri-terra)',
                  border: '1px solid rgba(168,85,247,0.4)',
                  borderRadius: 6,
                  padding: '6px 12px',
                  fontSize: '0.8rem',
                  textDecoration: 'none',
                }}
              >
                Open Bulk Decision matrix →
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Status glyph
// ============================================================================

const StatusGlyph: React.FC<{ status: TableStatus }> = ({ status }) => {
  if (status === 'pending') {
    return <span style={{ width: 18, color: 'var(--tri-body)' }} aria-label="pending">○</span>;
  }
  if (status === 'running') {
    return (
      <span
        style={{
          width: 18,
          height: 18,
          border: '2px solid var(--tri-taupe)',
          borderTop: '2px solid var(--tri-teal)',
          borderRadius: '50%',
          animation: 'compass-bulk-spin 0.8s linear infinite',
          display: 'inline-block',
        }}
        aria-label="running"
      />
    );
  }
  if (status === 'done') {
    return <span style={{ width: 18, color: 'var(--tri-good)' }} aria-label="done">✓</span>;
  }
  return <span style={{ width: 18, color: 'var(--tri-hard)' }} aria-label="failed">✗</span>;
};

// ============================================================================
// Utilities
// ============================================================================

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

// One-time keyframes injection for the spinner.
if (typeof document !== 'undefined' && !document.getElementById('compass-bulk-spin-keyframes')) {
  const style = document.createElement('style');
  style.id = 'compass-bulk-spin-keyframes';
  style.textContent = '@keyframes compass-bulk-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}

export default DatabricksBulkAnalyzeProgress;
